//@flow
import React, {useEffect, useState} from 'react';
import {
  makeStyles,
  Theme,
  MenuItem,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import {SCHEDULE_MESSAGE_SEND_TYPE} from '../../../../entities';
import {colors} from '../../../../assets/styles/colors';
import TiendadaCalendar from '../../../../elements/tiendada-calendar';
import {getStringDate, convertToLocalTime} from '../../../../utils/date';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useForm} from 'react-hook-form';
import {SCHEDULE_MESSAGE_FORM_FIELDS} from '../constants/constants';
import {useCurrentConversation} from '../../chat/utils/conversation';
import {useScheduleMessageActions} from '../utils';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';

import type {ScheduleMessage} from '../../../../entities';
import {useAlert} from '../../../../elements/Alert/AlertContext';
import {SCHEDULE_HOUR_PRESET_OPTIONS} from '../../constants/executionTime';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    top: 0,
    zIndex: 10,
    left: 0,
    width: '100%',
    height: '100%',
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
  },
  title: {
    color: colors.grey900,
    fontSize: '1.5rem',
    textAlign: 'center',
    fontWeight: 600,
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    width: '100%',
  },
  formLabel: {
    color: colors.grey900,
    fontSize: '1rem',
    fontWeight: 600,
  },
  subtitle: {
    color: colors.grey700,
    fontSize: '.875rem',
  },
  dateText: {
    color: colors.grey900,
    fontSize: '1rem',
  },
  formMessageInput: {
    padding: '.5rem',
    borderRadius: '0.5rem',
    border: `1px solid ${colors.grey300}`,
    outline: 'none',
    fontSize: '.875rem',
    color: colors.grey900,
    fontFamily: 'inherit',
    resize: 'none',
    '&::placeholder': {
      color: colors.grey500,
    },

    '&[data-error="true"]': {
      borderColor: colors.danger,
    },
    '&[data-error="true"]::placeholder': {
      color: colors.danger,
    },
  },
  dateSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    [theme.breakpoints.up('sm')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
    },
  },
  scheduleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flexGrow: 1,
  },
  footer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  submitButton: {
    width: '100%',
    height: '3rem',
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    maxWidth: '300px',
  },
}));

type Props = {
  onSuccessfulSubmit: () => void,
  scheduleMessage?: ScheduleMessage,
};

const getDefaultSendTime = (scheduleMessage: ScheduleMessage) => {
  const dateString = new Date(scheduleMessage.sendAt);
  const hour = dateString
    .getHours()
    .toString()
    .padStart(2, '0');
  const minute = dateString
    .getMinutes()
    .toString()
    .padStart(2, '0');

  return `${hour}:${minute}`;
};

export default function ScheduleMessageForm(props: Props) {
  const {onSuccessfulSubmit, scheduleMessage} = props;

  const classes = useStyles();

  const [selectedDate, handleDateChange] = useState<Date>(
    scheduleMessage ? new Date(scheduleMessage.sendAt) : new Date()
  );

  const {showAlert} = useAlert();

  const {currentConversation} = useCurrentConversation();
  const {
    createScheduleMessages,
    editScheduleMessages,
    loading,
    error,
  } = useScheduleMessageActions();

  useEffect(() => {
    if (!error) return;
    showAlert(error, {
      type: 'error',
      duration: 5000,
    });
  }, [error, showAlert]);

  const {
    register,
    setValue,
    handleSubmit,
    errors,
    clearErrors,
    watch,
  } = useForm({
    defaultValues: {
      [SCHEDULE_MESSAGE_FORM_FIELDS.SEND_DATE]: selectedDate,
      [SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TYPE]:
        scheduleMessage?.crmScheduledMessageSendType ||
        SCHEDULE_MESSAGE_SEND_TYPE.DEFAULT,
      [SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE]: scheduleMessage?.message.text,
      [SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TIME]:
        scheduleMessage && getDefaultSendTime(scheduleMessage),
    },
  });

  useEffect(() => {
    if (!register) return;
    register(SCHEDULE_MESSAGE_FORM_FIELDS.SEND_DATE, {required: true});
    register(SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TYPE, {required: true});
    register(SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE, {required: true});
    register(SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TIME, {required: true});
  }, [register]);

  if (!currentConversation) return null;

  const handleSetValue = (name, value) => {
    setValue(name, value);
    clearErrors(name);
  };

  const onSubmit = data => {
    const time = data[SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TIME];
    const date = data[SCHEDULE_MESSAGE_FORM_FIELDS.SEND_DATE];

    const sendAt = new Date(
      new Date(date).toISOString().split('T')[0] + 'T' + time
    );

    if (scheduleMessage) {
      return editScheduleMessages(scheduleMessage.id, {
        crmScheduledMessageSendType:
          data[SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TYPE],
        // $FlowFixMe
        message: {
          // messageType: 'TEXT',
          text: data[SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE],
        },
        sendAt: sendAt.toISOString(),
      }).then(result => {
        if (!result) return;
        showAlert('Message actualizado', {
          type: 'success',
          duration: 3000,
        });

        onSuccessfulSubmit();
      });
    }

    createScheduleMessages(currentConversation.id, {
      crmScheduledMessageSendType: data[SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TYPE],
      // $FlowFixMe
      message: {
        // messageType: 'TEXT',
        text: data[SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE],
      },
      sendAt: sendAt.toISOString(),
    }).then(result => {
      if (!result) return;
      showAlert('Mensaje programado correctamente', {
        type: 'success',
        duration: 3000,
      });
      onSuccessfulSubmit();
    });
  };

  const watchSendType = watch(SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TYPE);
  const watchMessage = watch(SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE);
  const watchSendTime = watch(SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TIME);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.root}>
      {loading && (
        <div className={classes.loader}>
          <InlineLoader loading={loading} size={48} />
        </div>
      )}
      <header className={classes.header}>
        <span className={classes.title}>Enviar mensaje programado</span>
      </header>

      <label className={classes.formField}>
        <span className={classes.formLabel}>Mensaje</span>

        <textarea
          className={classes.formMessageInput}
          name={SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE}
          data-error={!!errors[SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE]}
          placeholder={
            errors[SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE]
              ? 'Campo requerido'
              : 'Escribe un mensaje'
          }
          rows={4}
          value={watchMessage}
          onChange={event =>
            handleSetValue(
              SCHEDULE_MESSAGE_FORM_FIELDS.MESSAGE,
              event.target.value
            )
          }
        ></textarea>
      </label>

      <div className={classes.formField}>
        <span className={classes.formLabel}>Seleccionar fecha y hora</span>
        <div className={classes.dateSection}>
          <div className={classes.calendar}>
            <TiendadaCalendar
              initialDate={selectedDate}
              onChange={date => {
                handleDateChange(date);
                setValue(SCHEDULE_MESSAGE_FORM_FIELDS.SEND_DATE, date);
              }}
            />
          </div>
          <div className={classes.scheduleSection}>
            <div className={classes.formField}>
              <span className={classes.subtitle}>Fecha</span>
              <span className={classes.dateText}>
                {getStringDate(selectedDate)}
              </span>
            </div>

            <div className={classes.formField}>
              <span className={classes.subtitle}>Hora</span>
              <CustomSelect
                placeholder="Selecciona un horario"
                defaultValue="DEFAULT"
                helperText={
                  errors[SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TIME] &&
                  'Selecciona un horario'
                }
                onChange={e => {
                  handleSetValue(
                    SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TIME,
                    e.target.value
                  );
                }}
                value={watchSendTime}
              >
                <MenuItem
                  className={classes.itemSelectFontColor}
                  value="DEFAULT"
                >
                  Selecciona un horario
                </MenuItem>
                {SCHEDULE_HOUR_PRESET_OPTIONS.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    className={classes.itemSelectFontColor}
                  >
                    {`${option.label} - ${convertToLocalTime(option.utcValue)}`}
                  </MenuItem>
                ))}
              </CustomSelect>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.formField}>
        <span className={classes.formLabel}>Condición de envío</span>

        <FormControl>
          <RadioGroup
            defaultValue={SCHEDULE_MESSAGE_SEND_TYPE.DEFAULT}
            value={watchSendType}
            onChange={e => {
              setValue(SCHEDULE_MESSAGE_FORM_FIELDS.SEND_TYPE, e.target.value);
            }}
          >
            <FormControlLabel
              value={SCHEDULE_MESSAGE_SEND_TYPE.DEFAULT}
              control={<Radio color="primary" />}
              label="Enviar de todos modos"
            />
            <FormControlLabel
              value={SCHEDULE_MESSAGE_SEND_TYPE.CLIENT_DOES_NOT_RESPOND}
              control={<Radio color="primary" />}
              label="Enviar solo si no recibí respuesta"
            />
          </RadioGroup>
        </FormControl>
      </div>

      <footer className={classes.footer}>
        <CustomButton
          variant="contained"
          type="submit"
          className={classes.submitButton}
        >
          Programar envío
        </CustomButton>
      </footer>
    </form>
  );
}
